/**
 * 3D Model functionality for the enhanced grid system
 * Handles Three.js implementation and 3D model rendering
 */

window.ThreeDModel = (function() {
  // Private variables
  let THREE, GLTFLoader;
  let gridElement;
  let assetsTracker;
  let updateProgress;
  let modelContainer;
  
  /**
   * Initialize 3D model functionality
   * @param {Element} grid - The grid container element
   * @param {Object} threeJs - The Three.js library
   * @param {Object} gltfLoader - The GLTFLoader
   * @param {Object} assets - Asset tracking object
   * @param {Function} progressCallback - Function to call when progress updates
   */
  function init(grid, threeJs, gltfLoader, assets, progressCallback) {
    console.log('Initializing 3D model functionality');
    
    // Store references
    gridElement = grid;
    THREE = threeJs;
    GLTFLoader = gltfLoader;
    assetsTracker = assets;
    updateProgress = progressCallback;
    
    // Create and set up the 3D model
    createGLBModel();
  }
  
  /**
   * Create and set up the 3D GLB model element
   */
  function createGLBModel() {
    console.log('Creating 3D model with transparent background');
    
    // Check if model container already exists to prevent duplicates
    if (document.getElementById('glb-model-container')) {
      console.log('GLB model container already exists, skipping creation');
      return;
    }
    
// Create container for the model
    modelContainer = document.createElement('div');
    modelContainer.id = 'glb-model-container';
    modelContainer.classList.add('draggable-element');

    // Add click event for redirect - this will be handled by the draggable.js module
    // We removed the direct click handler here as it was conflicting with the drag logic
    
    // Create canvas for 3D rendering
    const canvas = document.createElement('canvas');
    
    // Create teleport popup for confirmation
    const teleportPopup = document.createElement('div');
    teleportPopup.className = 'teleport-popup';
    
    // Create label for "ARE YOU SURE?" text (moved inside popup)
    const teleportLabel = document.createElement('div');
    teleportLabel.textContent = 'SOON';//'ARE YOU SURE?';
    teleportLabel.className = 'teleport-label';
    
    // Create yes option
    const yesOption = document.createElement('div');
    yesOption.textContent = 'OK';
    yesOption.className = 'yes-option';
    yesOption.addEventListener('click', function(e) {
      e.stopPropagation();
      // Hide the popup
      teleportPopup.classList.remove('active');
    });
    /*yesOption.addEventListener('click', function(e) {
      e.stopPropagation();
      // Redirect to the target page
      window.location.href = '/pages/testtest';
    });*/
    
    // Create no option
    /*const noOption = document.createElement('div');
    noOption.textContent = 'NO';
    noOption.className = 'no-option';
    noOption.addEventListener('click', function(e) {
      e.stopPropagation();
      // Hide the popup
      teleportPopup.classList.remove('active');
    });*/
    
    // Add elements to the popup
    teleportPopup.appendChild(teleportLabel);
    teleportPopup.appendChild(yesOption);
    //teleportPopup.appendChild(noOption);
    
    // Add elements to the model container
    modelContainer.appendChild(canvas);
    modelContainer.appendChild(teleportPopup);
    
    // Add to grid
    gridElement.appendChild(modelContainer);
    
    // Add loading text until 3D model is ready
    const loadingText = document.createElement('div');
    loadingText.textContent = 'Loading 3D Model...';
    loadingText.className = 'loading-text';
    modelContainer.appendChild(loadingText);
    
    // Load the GLB model
    loadGLBModel(modelContainer, canvas, loadingText);
    
    // Add drag events to the model container (handled by DraggableElements module)
    if (window.DraggableElements && window.DraggableElements.dragStart) {
      modelContainer.addEventListener('mousedown', window.DraggableElements.dragStart);
      modelContainer.addEventListener('touchstart', window.DraggableElements.dragStart, { passive: false });
    }
    
    // Position the model
    positionModelRandomly();
  }
  
  /**
   * Position 3D model randomly but in the center area
   */
  function positionModelRandomly() {
    if (!modelContainer) return;
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Define model dimensions (from CSS)
    const modelHeight = viewportHeight * 0.3; // 30vh
    const modelWidth = modelHeight * 0.15;    // 15% of 30vh
    
    // More aggressive safety margin (increased from 3% to 5%)
    const safetyMargin = Math.max(viewportWidth * 0.05, 20); // At least 20px safety margin
    
    // Create strict boundaries for positioning
    const strictMinX = safetyMargin;
    const strictMaxX = viewportWidth - modelWidth - safetyMargin;
    const strictMinY = safetyMargin;
    const strictMaxY = viewportHeight - modelHeight - safetyMargin;
    
    // Validate boundaries for edge cases
    // If there's not enough space, adjust the area to ensure placement
    if (strictMaxX <= strictMinX || strictMaxY <= strictMinY) {
      console.warn('Viewport too small, adjusting model placement logic');
      
      // Force model to a specific corner (top-left with minimal margins)
      modelContainer.style.left = `${Math.max(5, strictMinX)}px`;
      modelContainer.style.top = `${Math.max(5, strictMinY)}px`;
      return;
    }
    
    // Calculate product layout area (center 50%)
    const centerX = viewportWidth / 2;
    const centerY = viewportHeight / 2;
    const centerWidth = viewportWidth * 0.5;
    const centerHeight = viewportHeight * 0.5;
    
    // Define product zone to avoid
    const productZoneMinX = centerX - (centerWidth / 2);
    const productZoneMaxX = centerX + (centerWidth / 2);
    const productZoneMinY = centerY - (centerHeight / 2);
    const productZoneMaxY = centerY + (centerHeight / 2);
    
    // Define four potential placement zones (avoiding center)
    const zones = [
      {
        name: 'top-left',
        minX: strictMinX,
        maxX: Math.min(productZoneMinX, strictMaxX),
        minY: strictMinY,
        maxY: Math.min(productZoneMinY, strictMaxY)
      },
      {
        name: 'top-right',
        minX: Math.max(productZoneMaxX, strictMinX),
        maxX: strictMaxX,
        minY: strictMinY,
        maxY: Math.min(productZoneMinY, strictMaxY)
      },
      {
        name: 'bottom-left',
        minX: strictMinX,
        maxX: Math.min(productZoneMinX, strictMaxX),
        minY: Math.max(productZoneMaxY, strictMinY),
        maxY: strictMaxY
      },
      {
        name: 'bottom-right',
        minX: Math.max(productZoneMaxX, strictMinX),
        maxX: strictMaxX,
        minY: Math.max(productZoneMaxY, strictMinY),
        maxY: strictMaxY
      }
    ];
    
    // Filter out invalid zones (where maxX <= minX or maxY <= minY)
    const validZones = zones.filter(zone => zone.maxX > zone.minX && zone.maxY > zone.minY);
    
    // Check if we have any valid zones
    let posX, posY;
    
    if (validZones.length > 0) {
      // Randomly select one of the valid zones
      const selectedZone = validZones[Math.floor(Math.random() * validZones.length)];
      console.log(`Positioning 3D model in ${selectedZone.name} zone`);
      
      // Generate random position within selected zone
      posX = selectedZone.minX + (Math.random() * (selectedZone.maxX - selectedZone.minX));
      posY = selectedZone.minY + (Math.random() * (selectedZone.maxY - selectedZone.minY));
    } else {
      // Fallback: If no valid zones, use a safe position along the edge
      console.warn('No valid corner zones found, using fallback positioning');
      
      // Choose a position along one of the edges
      const edgePosition = Math.floor(Math.random() * 4);
      switch (edgePosition) {
        case 0: // Top edge
          posX = strictMinX + (Math.random() * (strictMaxX - strictMinX));
          posY = strictMinY;
          break;
        case 1: // Right edge
          posX = strictMaxX;
          posY = strictMinY + (Math.random() * (strictMaxY - strictMinY));
          break;
        case 2: // Bottom edge
          posX = strictMinX + (Math.random() * (strictMaxX - strictMinX));
          posY = strictMaxY;
          break;
        case 3: // Left edge
          posX = strictMinX;
          posY = strictMinY + (Math.random() * (strictMaxY - strictMinY));
          break;
      }
    }
    
    // Final boundary enforcement (absolute guarantee)
    posX = Math.max(strictMinX, Math.min(posX, strictMaxX));
    posY = Math.max(strictMinY, Math.min(posY, strictMaxY));
    
    // Apply position
    modelContainer.style.left = `${posX}px`;
    modelContainer.style.top = `${posY}px`;
    
    console.log(`3D model positioned at X:${posX}, Y:${posY} (bounds: ${strictMinX}-${strictMaxX}, ${strictMinY}-${strictMaxY})`);
  }
  
  /**
   * Load the GLB model directly from specified URL
   */
  function loadGLBModel(modelContainer, canvas, loadingText) {
    console.log('Loading GLB model from specified URL');
    
    const modelUrl = 'https://cdn.shopify.com/s/files/1/0048/1155/4906/files/logo.glb?v=1685101170';
    
    // Check if GLTFLoader is available
    if (!GLTFLoader) {
      console.error('GLTFLoader not available. Cannot load GLB model.');
      // Remove loading text
      if (loadingText) loadingText.remove();
      return;
    }
    
    const loader = new GLTFLoader();
    loader.load(
      modelUrl,
      (gltf) => {
        console.log('Successfully loaded GLB model');
        processModel(gltf, modelContainer, canvas);
        
        // Mark GLB model as loaded
        if (assetsTracker) {
          assetsTracker.glbModel.loaded = true;
          if (updateProgress) updateProgress();
        }
        
        // Remove loading text
        if (loadingText) loadingText.remove();
      },
      (xhr) => {
        // Progress callback
        if (xhr.total > 0) {
          const progress = Math.round((xhr.loaded / xhr.total) * 100);
          console.log(`Loading GLB: ${progress}%`);
        }
      },
      (error) => {
        console.error('Failed to load GLB model:', error);
        
        // Mark as loaded even though it failed
        if (assetsTracker) {
          assetsTracker.glbModel.loaded = true;
          if (updateProgress) updateProgress();
        }
        
        // Remove loading text
        if (loadingText) loadingText.remove();
      }
    );
  }
  
  /**
   * Process the GLB model once loaded
   * @param {Object} gltf - The loaded GLB model
   * @param {Element} modelContainer - The container element
   * @param {HTMLCanvasElement} canvas - The canvas element for rendering
   */
  function processModel(gltf, modelContainer, canvas) {
    // Get container dimensions
    const rect = modelContainer.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    
    // Set canvas dimensions to match container
    canvas.width = containerWidth;
    canvas.height = containerHeight;
    
    const scene = new THREE.Scene();
    const aspectRatio = containerWidth / containerHeight;
    const camera = new THREE.PerspectiveCamera(40, aspectRatio, 0.1, 100);
    camera.position.z = 10;
    
    const renderer = new THREE.WebGLRenderer({ 
      canvas: canvas,
      antialias: true,
      alpha: true
    });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setClearColor(0x000000, 0);
    
    // Add lighting
    const pointLight1 = new THREE.PointLight(0xffffff, 1);
    pointLight1.position.set(500, 500, 500);
    scene.add(pointLight1);
    
    const pointLight2 = new THREE.PointLight(0xffffff, 0.25);
    pointLight2.position.set(-500, -500, -500);
    scene.add(pointLight2);
    
    // Create a parent object to properly center the model
    let modelParent = new THREE.Object3D();
    scene.add(modelParent);
    
    const model = gltf.scene;
    
    // Traverse the scene and make all materials black
    model.traverse((node) => {
      if (node.isMesh && node.material) {
        // Convert any material to MeshStandardMaterial with black color
        const material = new THREE.MeshStandardMaterial({
          color: 0x000000,
          roughness: 0.5,
          metalness: 0.8
        });
        
        // If it's an array of materials, convert each one
        if (Array.isArray(node.material)) {
          node.material.forEach((mat, index) => {
            node.material[index] = material.clone();
          });
        } else {
          node.material = material;
        }
      }
    });
    
    // Set the scale to 0.05 as requested
    model.scale.set(0.05, 0.05, 0.05);
    
    // Calculate the bounding box to center the model properly
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Adjust model positioning
    model.position.set(
      -center.x,
      -center.y + (size.y * 0.1),
      -center.z
    );
    
    // Add the model to the parent object
    modelParent.add(model);
    
    // Track rotation animation
    let yRotation = 0;
    
    // Animation function
    function animate() {
      requestAnimationFrame(animate);
      
      // Check if container size has changed
      const newRect = modelContainer.getBoundingClientRect();
      if (newRect.width !== containerWidth || newRect.height !== containerHeight) {
        // Update renderer and camera if size changed
        canvas.width = newRect.width;
        canvas.height = newRect.height;
        renderer.setSize(newRect.width, newRect.height);
        camera.aspect = newRect.width / newRect.height;
        camera.updateProjectionMatrix();
      }
      
      // Update rotation
      yRotation += 0.005;
      modelParent.rotation.y = yRotation;
      
      renderer.render(scene, camera);
    }
    
    // Start animation
    animate();
  }

  // Public API
  return {
    init: init,
    createGLBModel: createGLBModel,
    positionModelRandomly: positionModelRandomly
  };
})();
